/**
 * Offline cache – keeps the last loaded data of the user in IndexedDB
 *
 * On the next start the app shows this data at once and loads the current
 * data in the background, also without network. Only lists the fetch
 * replaces completely are kept: merged ones (calendar ranges, external
 * entries) would keep entries deleted in the meantime.
 */
import { createAction } from "@reduxjs/toolkit";
import type { Store } from "@reduxjs/toolkit";
import type { RootState } from "./store";
import { isDemoMode } from "./demoMode";

// Raise when the shape of the cached data changes, old caches are then ignored
const CACHE_VERSION = 1;
const MAX_AGE_MS = 14 * 24 * 60 * 60_000;
const SAVE_DELAY_MS = 1000;

const DB_NAME = "daex-cache";
const STORE_NAME = "state";

export type CachedSlices = {
  tasks: Pick<
    RootState["tasks"],
    "tasks" | "projectTasks" | "collections" | "projects"
  >;
  calendar: Pick<RootState["calendar"], "schedule" | "routines">;
  user: Pick<RootState["user"], "userData">;
  settings: Pick<RootState["settings"], "appLevel" | "settings">;
  objectives: Pick<RootState["objectives"], "objectives">;
};

type CacheEntry = { version: number; savedAt: number; data: CachedSlices };

export const hydrateFromCache = createAction<CachedSlices>("cache/hydrate");

// When the data restored on this start was saved
let restoredSavedAt: number | undefined;

/** Time of the cached data the app shows, undefined if none was restored */
export const getRestoredCacheTime = () => restoredSavedAt;

export const selectCachedSlices = (state: RootState): CachedSlices => {
  // The token stays in its own storage, the recording comes fresh from the server
  const userData = { ...state.user.userData, token: "" };
  delete userData.activeRecordingId;
  delete userData.activeTask;
  return {
    tasks: {
      tasks: state.tasks.tasks,
      projectTasks: state.tasks.projectTasks,
      collections: state.tasks.collections,
      projects: state.tasks.projects,
    },
    calendar: {
      schedule: state.calendar.schedule,
      routines: state.calendar.routines,
    },
    user: { userData },
    settings: {
      appLevel: state.settings.appLevel,
      settings: state.settings.settings,
    },
    objectives: { objectives: state.objectives.objectives },
  };
};

/** Lays the cached data over the slices, their status flags stay as they are */
export const mergeCachedSlices = (
  state: RootState,
  data: CachedSlices,
): RootState => ({
  ...state,
  tasks: { ...state.tasks, ...data.tasks },
  calendar: { ...state.calendar, ...data.calendar },
  user: {
    ...state.user,
    userData: { ...data.user.userData, token: state.user.userData.token },
  },
  settings: { ...state.settings, ...data.settings },
  objectives: { ...state.objectives, ...data.objectives },
});

export const cacheUserId = (state: RootState) =>
  state.auth.authData?.id || state.user.userData.id || "";

// ---------------------------
// IndexedDB
// ---------------------------

let dbPromise: Promise<IDBDatabase> | null = null;

const openDb = () => {
  if (!dbPromise) {
    dbPromise = new Promise((resolve, reject) => {
      if (typeof indexedDB === "undefined") {
        return reject(new Error("IndexedDB not available"));
      }
      const request = indexedDB.open(DB_NAME, 1);
      request.onupgradeneeded = () =>
        request.result.createObjectStore(STORE_NAME);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
    // Next call tries again, e.g. after private mode blocked it once
    dbPromise.catch(() => (dbPromise = null));
  }
  return dbPromise;
};

const run = async <T>(
  mode: IDBTransactionMode,
  operation: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> => {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, mode);
    const request = operation(transaction.objectStore(STORE_NAME));
    transaction.oncomplete = () => resolve(request.result);
    transaction.onerror = transaction.onabort = () =>
      reject(transaction.error);
  });
};

// ---------------------------
// Public API
// ---------------------------

/**
 * Puts the cached data of the user into the store.
 * Returns true if there was usable data.
 */
export const restoreCache = async (
  dispatch: Store["dispatch"],
  userId: string,
): Promise<boolean> => {
  if (isDemoMode() || !userId) return false;
  try {
    const entry = await run<CacheEntry | undefined>("readonly", (store) =>
      store.get(userId),
    );
    if (
      !entry ||
      entry.version !== CACHE_VERSION ||
      Date.now() - entry.savedAt > MAX_AGE_MS
    ) {
      return false;
    }
    dispatch(hydrateFromCache(entry.data));
    restoredSavedAt = entry.savedAt;
    return true;
  } catch (err) {
    console.warn("Offline cache could not be read:", err);
    return false;
  }
};

/** Deletes the cached data of all users, e.g. on logout */
export const clearCache = async () => {
  try {
    await run("readwrite", (store) => store.clear());
  } catch (err) {
    console.warn("Offline cache could not be cleared:", err);
  }
};

/** Saves the cached data shortly after it changed */
export const startCacheSync = (store: Store<RootState>) => {
  if (isDemoMode()) return () => {};

  let last = cachedSources(store.getState());
  let timer: ReturnType<typeof setTimeout> | undefined;

  const save = () => {
    timer = undefined;
    const state = store.getState();
    // Logged out in the meantime
    const userId = cacheUserId(state);
    if (!userId) return;
    const entry: CacheEntry = {
      version: CACHE_VERSION,
      savedAt: Date.now(),
      data: selectCachedSlices(state),
    };
    run("readwrite", (db) => db.put(entry, userId)).catch((err) =>
      console.warn("Offline cache could not be saved:", err),
    );
  };

  return store.subscribe(() => {
    const next = cachedSources(store.getState());
    if (next.every((source, i) => source === last[i])) return;
    last = next;
    if (!timer) timer = setTimeout(save, SAVE_DELAY_MS);
  });
};

// Immer keeps unchanged lists, so comparing the references is enough
const cachedSources = (state: RootState) => [
  state.tasks.tasks,
  state.tasks.projectTasks,
  state.tasks.collections,
  state.tasks.projects,
  state.calendar.schedule,
  state.calendar.routines,
  state.user.userData,
  state.settings.appLevel,
  state.settings.settings,
  state.objectives.objectives,
];
